import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  GetLabourList,
  CreateLabour,
  UpdateLabour,
  DeleteLabour,
} from "../../Services/SaleService";

/**
 * Labour State Interface with caching and optimization
 */
export interface LabourState {
  // List data
  labourCollection: any[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  
  // Detail data
  selectedLabourDetail: any | null;
  
  // Loading states
  loadingList: boolean;
  loadingDetail: boolean;
  loadingCreate: boolean;
  loadingUpdate: boolean;
  loadingDelete: boolean;
  
  // Error states
  error: string | null;
  errorDetail: string | null;
  
  // Metadata
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  
  // Cache management
  cache: Map<string, CacheEntry>;
  lastFetchParams: FetchParams | null;
  
  // Success flags for UX
  createSuccess: boolean;
  updateSuccess: boolean;
  deleteSuccess: boolean;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number;
}

interface FetchParams {
  consumerId: number;
  firmId: number;
  branchId: number;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  financialYear: string;
  saleType: string;
  startDate: string;
  endDate: string;
}

const CACHE_DURATION = 5 * 60 * 1000;

const generateCacheKey = (params: FetchParams): string => {
  return `labour_list_${params.consumerId}_${params.firmId}_${params.branchId}_${params.pageIndex}_${params.searchValue}_${params.saleType}`;
};

const isCacheValid = (entry: CacheEntry): boolean => {
  return Date.now() - entry.timestamp < entry.expiresIn;
};

export const fetchLabourList = createAsyncThunk(
  "labour/fetchLabourList",
  async (
    params: {
      consumerId: number;
      firmId: number;
      branchId: number;
      pageIndex: number;
      pageSize: number;
      sortColumn: string;
      sortDirection: string;
      searchValue: string;
      financialYear: string;
      saleType: string;
      startDate: string;
      endDate: string;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const root = getState() as { labour: LabourState };
      const cacheKey = generateCacheKey(params);
      
      if (root.labour.cache.has(cacheKey)) {
        const cached = root.labour.cache.get(cacheKey)!;
        if (isCacheValid(cached)) {
          return cached.data;
        }
      }

      const urlParams = new URLSearchParams();
      urlParams.append("consumerId", params.consumerId.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("pageIndex", params.pageIndex.toString());
      urlParams.append("pageSize", params.pageSize.toString());
      urlParams.append("sortColumn", params.sortColumn);
      urlParams.append("sortDirection", params.sortDirection);
      urlParams.append("searchValue", params.searchValue);
      urlParams.append("financialYear", params.financialYear);
      urlParams.append("saleType", params.saleType);
      urlParams.append("startDate", params.startDate);
      urlParams.append("endDate", params.endDate);

      const result: any = await GetLabourList(urlParams);
      
      if (result?.data?.data?.labourCollection) {
        return result.data.data;
      }
      
      return rejectWithValue("No labour data received");
    } catch (error: any) {
      console.error("Error fetching labour list:", error);
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch labour records"
      );
    }
  }
);

export const createLabourAsync = createAsyncThunk(
  "labour/createLabour",
  async (labourData: any, { rejectWithValue }) => {
    try {
      const result: any = await CreateLabour(labourData);
      if (result?.data) return result.data;
      return rejectWithValue("Failed to create labour");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to create labour"
      );
    }
  }
);

export const updateLabourAsync = createAsyncThunk(
  "labour/updateLabour",
  async (labourData: any, { rejectWithValue }) => {
    try {
      const result: any = await UpdateLabour(labourData);
      if (result?.data) return result.data;
      return rejectWithValue("Failed to update labour");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to update labour"
      );
    }
  }
);

export const deleteLabourAsync = createAsyncThunk(
  "labour/deleteLabour",
  async (deleteData: any, { rejectWithValue }) => {
    try {
      const result: any = await DeleteLabour(deleteData);
      if (result?.data) return deleteData;
      return rejectWithValue("Failed to delete labour");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to delete labour"
      );
    }
  }
);

const initialState: LabourState = {
  labourCollection: [],
  totalCount: 0,
  pageIndex: 1,
  pageSize: 10,
  selectedLabourDetail: null,
  loadingList: false,
  loadingDetail: false,
  loadingCreate: false,
  loadingUpdate: false,
  loadingDelete: false,
  error: null,
  errorDetail: null,
  sortColumn: "",
  sortDirection: "asc",
  searchValue: "",
  cache: new Map(),
  lastFetchParams: null,
  createSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
};

const labourSlice = createSlice({
  name: "labour",
  initialState,
  reducers: {
    clearLabourData: (state) => {
      state.labourCollection = [];
      state.totalCount = 0;
      state.selectedLabourDetail = null;
      state.error = null;
    },
    clearLabourDetail: (state) => {
      state.selectedLabourDetail = null;
      state.errorDetail = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.errorDetail = null;
    },
    clearSuccessFlags: (state) => {
      state.createSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
    },
    invalidateCache: (state) => {
      state.cache.clear();
    },
    setFetchMetadata: (state, action: PayloadAction<Partial<FetchParams>>) => {
      state.searchValue = action.payload.searchValue || state.searchValue;
      state.sortColumn = action.payload.sortColumn || state.sortColumn;
      state.sortDirection = action.payload.sortDirection || state.sortDirection;
      state.pageIndex = action.payload.pageIndex || state.pageIndex;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabourList.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchLabourList.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingList = false;
        state.labourCollection = action.payload?.labourCollection || [];
        state.totalCount = action.payload?.totalCount || 0;
        state.pageIndex = action.payload?.pageIndex || 1;
        state.pageSize = action.payload?.pageSize || 10;
        if (state.lastFetchParams) {
          const cacheKey = generateCacheKey(state.lastFetchParams);
          state.cache.set(cacheKey, {
            data: action.payload,
            timestamp: Date.now(),
            expiresIn: CACHE_DURATION,
          });
        }
      })
      .addCase(fetchLabourList.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createLabourAsync.pending, (state) => {
        state.loadingCreate = true;
        state.error = null;
        state.createSuccess = false;
      })
      .addCase(createLabourAsync.fulfilled, (state) => {
        state.loadingCreate = false;
        state.createSuccess = true;
        state.cache.clear();
      })
      .addCase(createLabourAsync.rejected, (state, action) => {
        state.loadingCreate = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateLabourAsync.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
        state.updateSuccess = false;
      })
      .addCase(updateLabourAsync.fulfilled, (state) => {
        state.loadingUpdate = false;
        state.updateSuccess = true;
        state.cache.clear();
      })
      .addCase(updateLabourAsync.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteLabourAsync.pending, (state) => {
        state.loadingDelete = true;
        state.error = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteLabourAsync.fulfilled, (state) => {
        state.loadingDelete = false;
        state.deleteSuccess = true;
        state.cache.clear();
      })
      .addCase(deleteLabourAsync.rejected, (state, action) => {
        state.loadingDelete = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearLabourData,
  clearLabourDetail,
  clearErrors,
  clearSuccessFlags,
  invalidateCache,
  setFetchMetadata,
} = labourSlice.actions;

export default labourSlice.reducer;
